import { getToken } from "./auth";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

function getAuthHeaders(): Record<string, string> {
  const token = getToken();
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `API error ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// Stats
export const fetchStats = () => apiFetch<{ data: Record<string, number> }>("/admin/stats");

// Users
export const fetchUsers = (params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString();
  return apiFetch<{ data: { users: unknown[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }>(
    `/admin/users?${qs}`,
  );
};

export const fetchUser = (id: string) =>
  apiFetch<{ data: Record<string, unknown> }>(`/admin/users/${encodeURIComponent(id)}`);

// Phonemes
export const fetchPhonemes = () => apiFetch<{ data: unknown[] }>("/admin/phonemes");
export const createPhoneme = (body: Record<string, unknown>) =>
  apiFetch<{ data: unknown }>("/admin/phonemes", { method: "POST", body: JSON.stringify(body) });
export const updatePhoneme = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ data: unknown }>(`/admin/phonemes/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
export const deletePhoneme = (id: string) =>
  apiFetch<void>(`/admin/phonemes/${encodeURIComponent(id)}`, { method: "DELETE" });

// Lessons
export const fetchLessons = () => apiFetch<{ data: unknown[] }>("/admin/lessons");
export const createLesson = (body: Record<string, unknown>) =>
  apiFetch<{ data: unknown }>("/admin/lessons", { method: "POST", body: JSON.stringify(body) });
export const updateLesson = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ data: unknown }>(`/admin/lessons/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
export const deleteLesson = (id: string) =>
  apiFetch<void>(`/admin/lessons/${encodeURIComponent(id)}`, { method: "DELETE" });

// Games
export const fetchGames = () => apiFetch<{ data: unknown[] }>("/admin/games");
export const createGame = (body: Record<string, unknown>) =>
  apiFetch<{ data: unknown }>("/admin/games", { method: "POST", body: JSON.stringify(body) });
export const updateGame = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ data: unknown }>(`/admin/games/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
export const deleteGame = (id: string) =>
  apiFetch<void>(`/admin/games/${encodeURIComponent(id)}`, { method: "DELETE" });

// Analytics
export const fetchEngagement = (period = "30d") =>
  apiFetch<{ data: Record<string, number> }>(`/admin/analytics/engagement?period=${encodeURIComponent(period)}`);
export const fetchLearning = (period = "30d") =>
  apiFetch<{ data: Record<string, number> }>(`/admin/analytics/learning?period=${encodeURIComponent(period)}`);
